import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Plus, Trash, QrCode, Gift, FileImage } from "@phosphor-icons/react";

const SEG_COLORS = ["#FF7A59", "#34C759", "#FFCC00", "#B5E2FA", "#E2D4F0", "#FFE9A8", "#DFF2EB", "#FF3B30"];

const TYPE_OPTIONS = [
  { value: "discount", label: "% Discount" },
  { value: "free_item", label: "Flat Reward" },
  { value: "no_reward", label: "Try Again" },
];

const VALUE_HELPERS = {
  discount: "Enter percentage (e.g., 10 = 10% off)",
  free_item: "Item name or amount (e.g., Free Coke, ₹50 Off)",
  no_reward: "No value needed",
};

// Try to detect mismatch between label and value (e.g., label '5% Off' but value '10')
function labelValueMismatch(label, type, value) {
  if (type === "no_reward") return null;
  if (!label || !value) return null;
  if (type === "discount") {
    const m = String(label).match(/(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const labelNum = parseFloat(m[1]);
    const valNum = parseFloat(value);
    if (!isFinite(labelNum) || !isFinite(valNum)) return null;
    if (Math.abs(labelNum - valNum) > 0.001) {
      return `Label says "${labelNum}" but value is "${valNum}"`;
    }
  }
  return null;
}

function emptySegment(idx = 0) {
  return {
    id: Math.random().toString(36).slice(2, 10),
    label: "",
    type: "discount",
    value: "10",
    weight: 25,
    color: SEG_COLORS[idx % SEG_COLORS.length],
  };
}

export default function OutletEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [reviewUrl, setReviewUrl] = useState("");
  const [requireScreenshot, setRequireScreenshot] = useState(false);
  const [allowMultipleSpins, setAllowMultipleSpins] = useState(false);
  const [segments, setSegments] = useState([
    { id: "s1", label: "5% Off", type: "discount", value: "5", weight: 30, color: "#FF7A59" },
    { id: "s2", label: "10% Off", type: "discount", value: "10", weight: 20, color: "#34C759" },
    { id: "s3", label: "Free Drink", type: "free_item", value: "Free Drink", weight: 10, color: "#FFCC00" },
    { id: "s4", label: "Try Again", type: "no_reward", value: "", weight: 40, color: "#B5E2FA" },
  ]);
  const [slug, setSlug] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const { data } = await api.get(`/outlets/${id}`);
        setName(data.name);
        setReviewUrl(data.google_review_url);
        setRequireScreenshot(!!data.require_screenshot);
        setAllowMultipleSpins(!!data.allow_multiple_spins);
        setSegments(data.segments);
        setSlug(data.slug);
      } catch (e) {
        toast.error("Failed to load outlet");
      } finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const totalWeight = segments.reduce((s, x) => s + (Number(x.weight) || 0), 0);

  const updateSeg = (i, patch) => {
    const copy = segments.slice();
    copy[i] = { ...copy[i], ...patch };
    setSegments(copy);
  };

  const addSeg = () => {
    if (segments.length >= 10) return toast.error("Max 10 segments");
    setSegments([...segments, emptySegment(segments.length)]);
  };
  const removeSeg = (i) => {
    if (segments.length <= 2) return toast.error("Need at least 2 segments");
    setSegments(segments.filter((_, idx) => idx !== i));
  };

  const validate = () => {
    if (!String(name || '').trim()) return "Outlet name is required";
    if (!String(reviewUrl || '').trim()) return "Google review URL is required";
    if (segments.length < 2) return "Add at least 2 spinner segments";
    for (const s of segments) {
      if (!String(s.label || '').trim()) return "Every segment needs a label";
      if (s.type === "discount" && (Number(s.value) <= 0)) return "Discount segments need a positive value";
      if (s.type === "free_item" && !String(s.value || '').trim()) return "Free item segments need a name";
    }
    if (totalWeight <= 0) return "Total weight must be greater than 0";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        name: String(name || '').trim(),
        google_review_url: String(reviewUrl || '').trim(),
        require_screenshot: !!requireScreenshot,
        allow_multiple_spins: !!allowMultipleSpins,
        segments: segments.map((s) => ({
          id: s.id,
          label: String(s.label || '').trim(),
          type: s.type,
          value: String(s.value),
          weight: Number(s.weight) || 0,
          color: s.color,
        })),
      };
      const res = isNew
        ? await api.post("/outlets", payload)
        : await api.put(`/outlets/${id}`, payload);
      toast.success(isNew ? "Outlet created!" : "Saved!");
      navigate(`/outlet/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this outlet? This cannot be undone.")) return;
    try {
      await api.delete(`/outlets/${id}`);
      toast.success("Outlet deleted");
      navigate("/dashboard");
    } catch { toast.error("Failed to delete"); }
  };

  if (loading) {
    return <DashboardLayout><div className="text-[var(--ink-muted)]">Loading…</div></DashboardLayout>;
  }

  return (
    <DashboardLayout
      title={isNew ? "New outlet" : name || "Outlet"}
      actions={
        <>
          <Link to="/dashboard" className="btn-ghost" data-testid="editor-back-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
          {!isNew && (
            <Link to={`/outlet/${id}/qr`} className="btn-ghost" data-testid="editor-view-qr-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
              <QrCode size={16} weight="bold" /> View QR
            </Link>
          )}
          {!isNew && (
            <Link to={`/outlet/${id}/poster`} className="btn-ghost" data-testid="editor-view-poster-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
              <FileImage size={16} weight="bold" /> Poster
            </Link>
          )}
          <button onClick={save} disabled={saving} data-testid="editor-save-btn" className="btn-primary" style={{ padding: "8px 14px", fontSize: 13 }}>
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: basics */}
        <div className="lg:col-span-1">
          <div className="card-pop p-6 space-y-4">
            <h3 className="display text-lg font-extrabold">Basics</h3>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Outlet name</label>
              <input data-testid="editor-name-input" className="input-pop mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sunset Cafe — Downtown" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Google review URL</label>
              <input data-testid="editor-review-url-input" className="input-pop mt-1.5" value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://g.page/r/..." />
              <p className="text-xs text-[var(--ink-muted)] mt-1">
                Find this in Google Business Profile → "Get more reviews".
              </p>
            </div>
            {!isNew && slug && (
              <div className="pt-3 border-t border-[var(--border-soft)]">
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Public link</div>
                <div className="text-sm mt-1 break-all">/r/{slug}</div>
              </div>
            )}

            {/* Screenshot verification toggle */}
            <div className="pt-3 border-t border-[var(--border-soft)]">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  data-testid="editor-require-screenshot-toggle"
                  onClick={() => setRequireScreenshot(!requireScreenshot)}
                  className="mt-0.5 flex-shrink-0 h-6 w-11 rounded-full border-[1.5px] border-[var(--ink)] relative transition-colors"
                  style={{ background: requireScreenshot ? "var(--primary)" : "#fff" }}
                  aria-pressed={requireScreenshot}
                >
                  <span
                    className="absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white border border-[var(--ink)] transition-all"
                    style={{ left: requireScreenshot ? "calc(100% - 20px)" : "2px" }}
                  />
                </button>
                <div className="flex-1">
                  <div className="text-sm font-bold">Require screenshot proof</div>
                  <div className="text-xs text-[var(--ink-muted)] mt-0.5">
                    Customer must upload a screenshot of their Google review before spinning.
                  </div>
                </div>
              </div>
            </div>

            {/* Allow multiple spins (testing mode) */}
            <div className="pt-3 border-t border-[var(--border-soft)]">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  data-testid="editor-allow-multiple-spins-toggle"
                  onClick={() => setAllowMultipleSpins(!allowMultipleSpins)}
                  className="mt-0.5 flex-shrink-0 h-6 w-11 rounded-full border-[1.5px] border-[var(--ink)] relative transition-colors"
                  style={{ background: allowMultipleSpins ? "var(--primary)" : "#fff" }}
                  aria-pressed={allowMultipleSpins}
                >
                  <span
                    className="absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white border border-[var(--ink)] transition-all"
                    style={{ left: allowMultipleSpins ? "calc(100% - 20px)" : "2px" }}
                  />
                </button>
                <div className="flex-1">
                  <div className="text-sm font-bold flex items-center gap-2">
                    Allow multiple spins
                    <span className="tag" style={{ background: "var(--pastel-yellow)", fontSize: 9, padding: "2px 6px" }}>Testing</span>
                  </div>
                  <div className="text-xs text-[var(--ink-muted)] mt-0.5">
                    Lets the same customer spin the wheel repeatedly. Useful for QA; turn off before going live.
                  </div>
                </div>
              </div>
            </div>

            {!isNew && (
              <button onClick={remove} data-testid="editor-delete-btn" className="text-sm font-bold text-[var(--error)] hover:underline">
                Delete outlet
              </button>
            )}
          </div>
        </div>

        {/* Right: segments */}
        <div className="lg:col-span-2">
          <div className="card-pop p-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="display text-lg font-extrabold">Spinner segments</h3>
                <p className="text-sm text-[var(--ink-muted)]">Add rewards customers can win. Higher chance = higher probability.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="tag" data-testid="editor-total-weight">
                  Total chance: {totalWeight} ({totalWeight > 0 ? "auto-normalized to 100%" : "invalid"})
                </span>
                <button onClick={addSeg} data-testid="editor-add-segment-btn" className="btn-ghost" style={{ padding: "8px 14px", fontSize: 13 }}>
                  <Plus size={14} weight="bold" /> Add
                </button>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {segments.map((s, i) => {
                const pct = totalWeight > 0 ? ((Number(s.weight) || 0) / totalWeight * 100) : 0;
                const mismatch = labelValueMismatch(s.label, s.type, s.value);
                return (
                  <div key={s.id} data-testid={`segment-row-${i}`} className="rounded-xl border border-[var(--border-soft)] p-4 bg-white">
                    <div className="grid md:grid-cols-12 gap-3 items-start">
                      <div className="md:col-span-1">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-muted)]">Color</label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {SEG_COLORS.map((c) => (
                            <button key={c} aria-label={c} onClick={() => updateSeg(i, { color: c })}
                              className="h-6 w-6 rounded border border-[var(--ink)]"
                              style={{ background: c, outline: s.color === c ? "2px solid #1A1A1A" : "none", outlineOffset: 1 }}
                              type="button"
                            />
                          ))}
                        </div>
                      </div>
                      <div className="md:col-span-3">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-muted)]">Label</label>
                        <input data-testid={`segment-${i}-label-input`} className="input-pop mt-1.5" value={s.label} onChange={(e) => updateSeg(i, { label: e.target.value })} placeholder="10% Off" />
                      </div>
                      <div className="md:col-span-3">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-muted)]">Type</label>
                        <select data-testid={`segment-${i}-type-select`} className="input-pop mt-1.5" value={s.type} onChange={(e) => updateSeg(i, { type: e.target.value, value: e.target.value === "no_reward" ? "" : s.value })}>
                          {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-muted)]">Reward value</label>
                        <input data-testid={`segment-${i}-value-input`} disabled={s.type === "no_reward"} className="input-pop mt-1.5"
                          value={s.value} onChange={(e) => updateSeg(i, { value: e.target.value })}
                          placeholder={s.type === "discount" ? "10" : s.type === "free_item" ? "Free Coke" : "—"} />
                        <p className="text-[11px] text-[var(--ink-muted)] mt-1 leading-snug" data-testid={`segment-${i}-value-helper`}>
                          {VALUE_HELPERS[s.type]}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-[var(--ink-muted)]">
                          Chance (%) {pct > 0 && <span className="text-[var(--ink)]">· {pct.toFixed(0)}%</span>}
                        </label>
                        <input data-testid={`segment-${i}-weight-input`} type="number" min="0" className="input-pop mt-1.5" value={s.weight} onChange={(e) => updateSeg(i, { weight: e.target.value })} />
                        <p className="text-[11px] text-[var(--ink-muted)] mt-1 leading-snug">
                          Higher value = higher probability. Auto-normalized to 100%.
                        </p>
                      </div>
                      <div className="md:col-span-1 flex md:justify-end md:pt-6">
                        <button onClick={() => removeSeg(i)} data-testid={`segment-${i}-remove-btn`} className="h-10 w-10 rounded-lg border border-[var(--ink)] flex items-center justify-center hover:bg-red-50">
                          <Trash size={16} weight="bold" />
                        </button>
                      </div>
                    </div>
                    {mismatch && (
                      <div className="mt-3 text-xs font-semibold text-[var(--warning)] bg-[#FFF8DC] border border-[#D4A017] rounded-lg px-3 py-2 inline-flex items-center gap-2" data-testid={`segment-${i}-mismatch-warning`}>
                        <span aria-hidden="true">⚠</span> {mismatch}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-5 rounded-xl border border-dashed border-[var(--ink)] p-4 bg-[var(--secondary)]">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Gift size={16} weight="bold" /> Tip
              </div>
              <p className="text-sm text-[var(--ink-muted)] mt-1">
                Mix wins and "Try Again" segments. A higher weight means a higher chance of landing on that slice — totals are normalized automatically.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
